import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSearch, 
  faUser, 
  faCheck,
  faTimes,
  faSpinner,
  faExclamationTriangle,
  faStop,
  faChevronLeft,
  faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import api from '../../Api';
import ConfirmationModal from '../../components/ConfirmationModal';
import SuccessModal from '../../components/SuccessModal';

const CatiAssignments = () => {
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeOnly, setActiveOnly] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalAssignments, setTotalAssignments] = useState(0);
  const assignmentsPerPage = 10;
  const [stats, setStats] = useState({ total: 0, active: 0, finished: 0 });
  
  // Modals
  const [showEndModal, setShowEndModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [endAssignment, setEndAssignment] = useState(null);
  const [loadingEnd, setLoadingEnd] = useState(false);

  useEffect(() => {
    fetchAssignments();
  }, [currentPage, searchTerm, activeOnly]);

  const fetchAssignments = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        per_page: assignmentsPerPage,
        active_only: activeOnly
      });

      if (searchTerm) {
        params.append('search', searchTerm);
      }

      const response = await api.get(`/cati/assignments?${params}`);
      
      if (response.data.success) {
        // Laravel paginate returns data in response.data.data
        const paginatedData = response.data.data;
        setAssignments(paginatedData.data || []);
        setTotalPages(paginatedData.last_page || 1);
        setTotalAssignments(paginatedData.total || 0);
        setStats(response.data.stats || { total: 0, active: 0, finished: 0 });
      }
    } catch (error) {
      console.error('Error fetching assignments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleEndClick = (assignment) => {
    setEndAssignment(assignment);
    setShowEndModal(true);
  };

  const handleEndConfirm = async () => {
    if (!endAssignment) return;

    try {
      setLoadingEnd(true);
      // Backend will use the current server time when ending the assignment
      const response = await api.put(`/cati/assignments/${endAssignment.id}/end`, {});
      
      if (response.data.success) {
        setSuccessMessage('Assignation terminée avec succès');
        setShowSuccessModal(true);
        fetchAssignments();
      }
    } catch (error) {
      console.error('Error ending assignment:', error);
      setSuccessMessage('Erreur lors de la fin de l\'assignation');
      setShowSuccessModal(true);
    } finally {
      setLoadingEnd(false);
      setShowEndModal(false);
      setEndAssignment(null);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };


  return (
    <div className="min-h-screen bg-gray-50 relative overflow-hidden">
      {/* Dotted Background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(circle, #dc2626 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }}></div>
      </div>

      <div className="relative z-10 p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">ASSIGNATIONS CATI</h1>
              <p className="text-gray-600">Gestion des assignations des postes aux enquêteurs</p>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100 mb-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex items-center space-x-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="activeOnly"
                    checked={activeOnly}
                    onChange={(e) => {
                      setActiveOnly(e.target.checked);
                      setCurrentPage(1);
                    }}
                    className="mr-2 h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                  />
                  <label htmlFor="activeOnly" className="text-sm font-medium text-gray-700">
                    Actives seulement
                  </label>
                </div>
              </div>
              <div className="flex items-center space-x-4 w-full lg:w-auto">
                <div className="relative flex-1 lg:flex-initial">
                  <FontAwesomeIcon icon={faSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Rechercher par enquêteur ou poste..."
                    value={searchTerm}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 w-full lg:w-64"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100">
              <div className="flex items-center">
                <div className="p-3 bg-blue-100 rounded-full">
                  <FontAwesomeIcon icon={faUser} className="text-blue-600 text-xl" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Assignations</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total || 0}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100">
              <div className="flex items-center">
                <div className="p-3 bg-green-100 rounded-full">
                  <FontAwesomeIcon icon={faCheck} className="text-green-600 text-xl" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Actives</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.active || 0}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-xl border border-gray-100">
              <div className="flex items-center">
                <div className="p-3 bg-gray-100 rounded-full">
                  <FontAwesomeIcon icon={faTimes} className="text-gray-600 text-xl" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Terminées</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.finished || 0}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Assignments Table */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            {loading ? (
              <div className="p-8 text-center">
                <FontAwesomeIcon icon={faSpinner} className="text-4xl text-red-600 animate-spin mb-4" />
                <p className="text-gray-600">Chargement des assignations...</p>
              </div>
            ) : assignments.length === 0 ? (
              <div className="p-8 text-center">
                <FontAwesomeIcon icon={faExclamationTriangle} className="text-4xl text-gray-400 mb-4" />
                <p className="text-gray-600">Aucune assignation trouvée</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Poste
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Enquêteur
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Enquête(s)
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Début
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Fin
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Assigné par
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Statut
                        </th>
                        <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {assignments.map((assignment) => (
                        <tr key={assignment.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {assignment.post?.number}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {assignment.interviewer?.name}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            {assignment.surveys && assignment.surveys.length > 0 ? (
                              <div className="flex flex-col gap-1">
                                {assignment.surveys.map((survey, index) => {
                                  // Check if survey is deactivated (is_active field from database, not LimeSurvey)
                                  const isDeactivated = survey?.is_active === false;
                                  
                                  return (
                                    <div 
                                      key={survey?.id || index} 
                                      className={`text-sm ${
                                        isDeactivated 
                                          ? 'text-gray-400 opacity-60' 
                                          : 'text-gray-900'
                                      }`}
                                    >
                                      <span className="truncate" title={survey?.title || 'N/A'}>
                                        {survey?.title || 'N/A'}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div className="text-sm text-gray-400 italic">
                                Aucune enquête
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {formatDate(assignment.starts_at)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {formatDate(assignment.ends_at)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {assignment.assigned_by?.name || '-'}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {(() => {
                              const isFinished = assignment.ends_at && new Date(assignment.ends_at) < new Date();
                              return (
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                  isFinished 
                                    ? 'bg-gray-100 text-gray-800' 
                                    : 'bg-green-100 text-green-800'
                                }`}>
                                  {isFinished ? 'Terminée' : 'Active'}
                                </span>
                              );
                            })()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            {(() => {
                              // Don't show pause/stop button if end date has already passed
                              const now = new Date();
                              const endDate = assignment.ends_at ? new Date(assignment.ends_at) : null;
                              const hasEndDatePassed = endDate && endDate < now;
                              
                              // Only show button if assignment hasn't ended (no end date or end date hasn't passed)
                              if (hasEndDatePassed) {
                                return null;
                              }
                              
                              return (
                                <button
                                  onClick={() => handleEndClick(assignment)}
                                  className="text-red-600 hover:text-red-900 p-1"
                                  title="Terminer l'assignation"
                                >
                                  <FontAwesomeIcon icon={faStop} />
                                </button>
                              );
                            })()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {!loading && totalPages > 1 && (
                  <div className="mt-6 bg-white/80 backdrop-blur-md rounded-xl shadow-lg border border-white/20 p-4">
                    {/* Pagination controls */}
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                      {/* Mobile: Show page info */}
                      <div className="md:hidden text-sm text-gray-700 text-center w-full">
                        Page {currentPage} sur {totalPages}
                      </div>
                      
                      {/* Desktop: Show full info */}
                      <div className="hidden md:block text-sm text-gray-700">
                        Affichage de {((currentPage - 1) * assignmentsPerPage) + 1} à {Math.min(currentPage * assignmentsPerPage, totalAssignments)} sur {totalAssignments} assignations
                      </div>
                      
                      <div className="flex items-center space-x-2 w-full sm:w-auto justify-center sm:justify-end">
                        <button
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <FontAwesomeIcon icon={faChevronLeft} className="w-4 h-4" />
                        </button>
                        
                        <div className="flex space-x-1">
                          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                            let pageNum;
                            if (totalPages <= 5) {
                              pageNum = i + 1;
                            } else if (currentPage <= 3) {
                              pageNum = i + 1;
                            } else if (currentPage >= totalPages - 2) {
                              pageNum = totalPages - 4 + i;
                            } else {
                              pageNum = currentPage - 2 + i;
                            }
                            
                            return (
                              <button
                                key={pageNum}
                                onClick={() => handlePageChange(pageNum)}
                                className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                                  currentPage === pageNum
                                    ? 'bg-red-600 text-white'
                                    : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                                }`}
                              >
                                {pageNum}
                              </button>
                            );
                          })}
                        </div>
                        
                        <button
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          <FontAwesomeIcon icon={faChevronRight} className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* End Assignment Confirmation Modal */}
      <ConfirmationModal
        isOpen={showEndModal}
        onClose={() => {
          setShowEndModal(false);
          setEndAssignment(null);
        }}
        onConfirm={handleEndConfirm}
        title="Terminer l'assignation"
        message={`Êtes-vous sûr de vouloir terminer l'assignation du poste ${endAssignment?.post?.number} à ${endAssignment?.interviewer?.name} ?`}
        confirmText="Terminer"
        isLoading={loadingEnd}
      />

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        message={successMessage}
      />
    </div>
  );
};

export default CatiAssignments;
